// ============================================================
// E1/G1：3D 编辑器 AI 面板（单步 + Agent 多步）
// 对话/指令 → 服务端受约束计划 → 幽灵预览 → 应用/撤销/重新生成/丢弃
// G2：选中对象范围 = 服务端硬锁定（越界操作被丢弃）
// 应用走 E0 命令总线事务；AI 修改的模型打 aiGenerated 标记
// ============================================================
import { useEffect, useState } from 'react';
import { Button, Drawer, Input, Segmented, Space, Tag, Tooltip, message } from 'antd';
import { ClearOutlined, RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { commandBus } from '../../core/commandBus';
import useSceneStore from '../../stores/sceneStore';
import { useRegionSelectStore } from '../../stores/regionSelectStore';
import { useInspectFixStore } from '../../stores/inspectFixStore';
import {
  markAiEditorStatus,
  planAiEditor,
  planAiEditorAgent,
  type AgentPlanResponse,
  type EditorPlanResponse,
  type EditorOp,
} from '../../api/aiEditor';
import { clearAllAiMarks, markModelsAiGenerated, opsToCommands, type OpCommandBuild } from '../../utils/aiEditorOps';
import { useSceneBindingStore } from '../../stores/sceneBindingStore';

type StepStatus = 'pending' | 'running' | 'done' | 'error';

export default function AiEditorPanel() {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'global' | 'selection'>('global');
  const [mode, setMode] = useState<'single' | 'agent'>('single');
  // G2：区域框选（框选后自动打开面板，scope 切到 region + modelIds 白名单）
  const regionIds = useRegionSelectStore((s) => s.ids);
  const regionOpenRequest = useRegionSelectStore((s) => s.openRequest);
  const clearRegion = useRegionSelectStore((s) => s.clear);
  const effectiveScope: 'global' | 'selection' | 'region' = regionIds.length > 0 ? 'region' : scope;
  useEffect(() => {
    if (regionOpenRequest > 0) setOpen(true);
  }, [regionOpenRequest]);
  const [prompt, setPrompt] = useState('');
  // B4：渲染巡检 → 一键修复（预填指令 + 自动生成计划）
  const fixOpenRequest = useInspectFixStore((s) => s.openRequest);
  const fixPrompt = useInspectFixStore((s) => s.prompt);
  const fixAutoRun = useInspectFixStore((s) => s.autoRun);
  useEffect(() => {
    if (fixOpenRequest === 0) return;
    setOpen(true);
    setPrompt(fixPrompt);
    if (fixAutoRun) void generate(fixPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixOpenRequest]);
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState<EditorPlanResponse | null>(null);
  const [build, setBuild] = useState<OpCommandBuild | null>(null);
  const [applied, setApplied] = useState(false);
  const [agentPlan, setAgentPlan] = useState<AgentPlanResponse | null>(null);
  const [stepStatus, setStepStatus] = useState<StepStatus[]>([]);
  const [agentRunning, setAgentRunning] = useState(false);
  const selectedModelId = useSceneStore((s) => s.selectedModelId);
  const aiMarkedCount = useSceneStore((s) => s.models.filter((m) => m.aiGenerated).length);

  const collectContext = () => {
    const store = useSceneStore.getState();
    return {
      store,
      models: store.models.map((m) => ({ id: m.id, name: m.name, visible: m.visible, locked: !!m.locked })),
    };
  };

  const generate = async (overridePrompt?: string) => {
    const text = (overridePrompt ?? prompt).trim();
    if (!text) {
      message.warning('请输入指令，如「切换为夜景」「隐藏泵房」');
      return;
    }
    const { store, models } = collectContext();
    if (effectiveScope === 'selection' && !store.selectedModelId) {
      message.warning('请先在场景中选中模型');
      return;
    }
    setPlanning(true);
    setAgentPlan(null);
    setStepStatus([]);
    try {
      const regionPayload = effectiveScope === 'region' ? { modelIds: regionIds } : {};
      if (mode === 'agent') {
        const r = await planAiEditorAgent({
          prompt: text,
          scope: effectiveScope,
          ...(store.currentScene?.name ? { sceneName: store.currentScene.name } : {}),
          ...(effectiveScope === 'selection' && store.selectedModelId ? { selectedModelId: store.selectedModelId } : {}),
          ...regionPayload,
          models,
        });
        setAgentPlan(r);
        setStepStatus(r.steps.map(() => 'pending'));
      } else {
        const r = (await planAiEditor({
          prompt: text,
          scope: effectiveScope,
          ...(store.currentScene?.name ? { sceneName: store.currentScene.name } : {}),
          ...(effectiveScope === 'selection' && store.selectedModelId ? { selectedModelId: store.selectedModelId } : {}),
          ...regionPayload,
          models,
        })) as EditorPlanResponse;
        setPlan(r);
        setBuild(opsToCommands(r.ops as EditorOp[], store.models));
        setApplied(false);
      }
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'AI 计划生成失败');
    } finally {
      setPlanning(false);
    }
  };

  const applySingle = async () => {
    if (!plan || !build) return;
    const beforeIds = new Set(useSceneStore.getState().models.map((m) => m.id));
    const r = await commandBus.applyBatch(build.commands, { label: 'ai-editor' });
    if (!r.ok) {
      await markAiEditorStatus(plan.callId, { status: 'failed', ...(r.error ? { error: r.error } : {}) }).catch(() => undefined);
      message.error(r.error ?? '应用失败');
      return;
    }
    const after = useSceneStore.getState().models;
    const newIds = after.filter((m) => !beforeIds.has(m.id)).map((m) => m.id);
    const markIds = [...new Set([...build.touchedModelIds.filter((id) => after.some((m) => m.id === id)), ...newIds])];
    const marked = markModelsAiGenerated(markIds);
    await syncBindings(build.bindings);
    setApplied(true);
    await markAiEditorStatus(plan.callId, { status: 'applied' }).catch(() => undefined);
    message.success(`已应用 ${build.commands.length} 项操作${marked > 0 ? `，标记 ${marked} 个 AI 模型` : ''}（可撤销）`);
  };

  /** B1：把 AI 产出的 3D 数据绑定同步落库（设备联动等；失败不阻塞主流程） */
  const syncBindings = async (items: OpCommandBuild['bindings']) => {
    if (!items || items.length === 0) return;
    const sceneId = useSceneStore.getState().currentScene?.id;
    if (!sceneId) return;
    for (const b of items) {
      try {
        await useSceneBindingStore.getState().setVisualBinding(sceneId, b.modelId, b.modelName, b.visualType, {
          ...(b.deviceId ? { deviceId: b.deviceId } : {}),
          ...(b.bindPointKey ? { bindPointKey: b.bindPointKey } : {}),
          ...(b.ruleConfig ? { ruleConfig: b.ruleConfig } : {}),
        });
      } catch {
        message.warning(`绑定同步失败（${b.modelName}/${b.visualType}），可在「属性→数据绑定」手动保存`);
      }
    }
  };

  const runAgentStep = async (index: number): Promise<boolean> => {
    if (!agentPlan) return false;
    const step = agentPlan.steps[index];
    if (!step) return false;
    const store = useSceneStore.getState();
    const built = opsToCommands(step.ops as EditorOp[], store.models);
    if (built.commands.length === 0) {
      setStepStatus((s) => s.map((v, i) => (i === index ? 'error' : v)));
      message.warning(`步骤 ${index + 1} 无可执行操作`);
      return false;
    }
    setStepStatus((s) => s.map((v, i) => (i === index ? 'running' : v)));
    const beforeIds = new Set(useSceneStore.getState().models.map((m) => m.id));
    const r = await commandBus.applyBatch(built.commands, { label: `ai-agent:step${index + 1}` });
    if (!r.ok) {
      setStepStatus((s) => s.map((v, i) => (i === index ? 'error' : v)));
      message.error(`步骤 ${index + 1} 失败：${r.error}`);
      return false;
    }
    const after = useSceneStore.getState().models;
    const newIds = after.filter((m) => !beforeIds.has(m.id)).map((m) => m.id);
    markModelsAiGenerated([...new Set([...built.touchedModelIds.filter((id) => after.some((m) => m.id === id)), ...newIds])]);
    await syncBindings(built.bindings);
    setStepStatus((s) => s.map((v, i) => (i === index ? 'done' : v)));
    return true;
  };

  const runAllSteps = async () => {
    if (!agentPlan) return;
    setAgentRunning(true);
    try {
      for (let i = 0; i < agentPlan.steps.length; i++) {
        const ok = await runAgentStep(i);
        if (!ok) break;
      }
      const allDone = useSceneStore.getState().models.length >= 0; // 仅用于触发 message
      void allDone;
      await markAiEditorStatus(agentPlan.callId, { status: 'applied' }).catch(() => undefined);
      message.success('Agent 步骤执行完成（每步可单独撤销）');
    } finally {
      setAgentRunning(false);
    }
  };

  const undo = () => {
    commandBus.undo();
    setApplied(false);
    message.success('已撤销 AI 变更');
  };

  const discard = async () => {
    if (plan) await markAiEditorStatus(plan.callId, { status: 'discarded' }).catch(() => undefined);
    if (agentPlan) await markAiEditorStatus(agentPlan.callId, { status: 'discarded' }).catch(() => undefined);
    setPlan(null);
    setBuild(null);
    setApplied(false);
    setAgentPlan(null);
    setStepStatus([]);
  };

  const clearMarks = () => {
    const n = clearAllAiMarks();
    message.success(n > 0 ? `已清除 ${n} 个 AI 标记` : '当前没有 AI 标记');
  };

  const hasPlan = !!(plan || agentPlan);

  return (
    <>
      <Tooltip title="AI 助手（单步 / Agent 多步；预览后应用，可撤销）">
        <Button size="small" icon={<RobotOutlined />} onClick={() => setOpen(true)}>
          AI 助手
        </Button>
      </Tooltip>

      <Drawer title="AI 助手" open={open} onClose={() => setOpen(false)} size={440} destroyOnHidden>
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <Space size={12} wrap>
            <Segmented
              value={scope}
              onChange={(v) => setScope(v as 'global' | 'selection')}
              disabled={regionIds.length > 0}
              options={[
                { label: '全局', value: 'global' },
                { label: '选中对象', value: 'selection', disabled: !selectedModelId },
              ]}
            />
            <Segmented
              value={mode}
              onChange={(v) => setMode(v as 'single' | 'agent')}
              options={[
                { label: '单步', value: 'single' },
                { label: 'Agent 多步', value: 'agent' },
              ]}
            />
          </Space>
          {regionIds.length > 0 ? (
            <Space size={6} wrap data-testid="region-chip">
              <Tag color="blue">已框选 {regionIds.length} 个模型</Tag>
              <Button
                size="small"
                icon={<ClearOutlined />}
                data-testid="region-chip-clear"
                onClick={() => {
                  clearRegion();
                  message.info('已清除框选（恢复按范围选择）');
                }}
              >
                清除框选
              </Button>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>后续指令仅作用于框选模型（服务端硬约束）</span>
            </Space>
          ) : null}
          <Input.TextArea
            rows={3}
            placeholder={
              mode === 'agent'
                ? '如：切换为夜景，然后隐藏泵房并复制厂房（Agent 自动拆步，可逐步执行）'
                : '如：切换为夜景 / 隐藏泵房 / 把水池主色改成 #1C7ED6'
            }
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <Space>
            <Button type="primary" icon={<ThunderboltOutlined />} loading={planning} onClick={() => void generate()}>
              {mode === 'agent' ? '生成步骤' : '生成计划'}
            </Button>
            {hasPlan ? (
              <Button onClick={() => void generate()} loading={planning}>
                重新生成
              </Button>
            ) : null}
            {mode === 'agent' && agentPlan && stepStatus.some((s) => s === 'pending') ? (
              <Button type="primary" ghost loading={agentRunning} onClick={() => void runAllSteps()}>
                全部执行
              </Button>
            ) : null}
            {hasPlan ? <Button onClick={() => void discard()}>丢弃</Button> : null}
          </Space>
          {scope === 'selection' ? (
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>选区锁定：越出选中模型的操作会被服务端丢弃（G2）</div>
          ) : null}

          {/* 单步计划 */}
          {plan ? (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, background: '#fafcff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Tag color={plan.source === 'llm' ? 'geekblue' : 'orange'}>{plan.source === 'llm' ? 'AI 模型' : '规则兜底'}</Tag>
                <span style={{ fontSize: 12, color: '#595959' }}>{plan.summary}</span>
              </div>
              {build && build.descriptions.length > 0 ? (
                <ol style={{ margin: '4px 0 8px 18px', padding: 0, fontSize: 13 }}>
                  {build.descriptions.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ol>
              ) : (
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>无可执行操作（换一种说法试试）</div>
              )}
              <Space>
                <Button type="primary" data-ai-action="apply" disabled={!build || build.commands.length === 0 || applied} onClick={() => void applySingle()}>
                  应用
                </Button>
                {applied ? (
                  <Button data-ai-action="undo" onClick={undo}>
                    撤销
                  </Button>
                ) : null}
              </Space>
            </div>
          ) : null}

          {/* G1：Agent 多步计划 */}
          {agentPlan ? (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, background: '#fafcff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Tag color={agentPlan.source === 'llm' ? 'geekblue' : 'orange'}>{agentPlan.source === 'llm' ? 'AI 模型' : '规则兜底'}</Tag>
                <Tag color="purple">Agent · {agentPlan.steps.length} 步</Tag>
                <span style={{ fontSize: 12, color: '#595959' }}>{agentPlan.summary}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {agentPlan.steps.map((s, i) => (
                  <div key={i} data-agent-step={i + 1} data-agent-status={stepStatus[i] ?? 'pending'} style={{ border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Tag color={stepStatus[i] === 'done' ? 'green' : stepStatus[i] === 'error' ? 'red' : 'default'}>
                        {i + 1}/{agentPlan.steps.length}
                      </Tag>
                      <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{s.title}</span>
                      <Button
                        size="small"
                        data-agent-action="run"
                        disabled={stepStatus[i] === 'done' || agentRunning}
                        loading={stepStatus[i] === 'running'}
                        onClick={() => void runAgentStep(i)}
                      >
                        执行
                      </Button>
                    </div>
                    {s.reason ? <div style={{ fontSize: 12, color: '#1677ff', marginTop: 2 }}>{s.reason}</div> : null}
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>{s.ops.length} 项操作</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="small" data-ai-action="undo" ghost onClick={undo} disabled={!stepStatus.some((s) => s === 'done')}>
                  撤销上一步
                </Button>
              </div>
            </div>
          ) : null}

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, fontSize: 12, color: '#8c8c8c' }}>
            <Space>
              <span>AI 标记：{aiMarkedCount} 个模型</span>
              <Button size="small" icon={<ClearOutlined />} disabled={aiMarkedCount === 0} onClick={clearMarks}>
                清除全部 AI 标记
              </Button>
            </Space>
          </div>
        </Space>
      </Drawer>
    </>
  );
}
